import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MoreHorizontal, ChevronRight } from 'lucide-react';
import Tooltip from '../ui/Tooltip';
import ExportSubmenu from './ExportSubmenu';
import './DocumentMoreMenu.css';
import { MenuIcon } from './menuIcons';
import { moreMenuGroups, nextMenuIndex } from './moreMenuItems';

/**
 * The toolbar's "…" menu (card 3.1): grouped entries with their shortcuts and the
 * Export ▸ submenu (card 3.2), opening to the left.
 *
 * Keyboard (WAI-ARIA menu): opening focuses the first entry; Up/Down/Home/End
 * move (wrapping, skipping disabled ones); Enter/Space run; Right on Export
 * opens the submenu, Left/Escape there comes back; Escape closes and returns
 * focus to the button; Tab closes. Hovering Export opens the submenu too —
 * without highlighting a format (VS Code / the artboard): `subOpen` is
 * 'keys' or 'mouse', and only 'keys' puts the focus on PDF.
 */
export default function DocumentMoreMenu({ pinned, canMove, canPin, onAction, onExport, onOpenChange }) {
  const { t } = useTranslation();
  const [open, setOpenState] = useState(false);
  const [subOpen, setSubOpen] = useState(false);
  const [focusIndex, setFocusIndex] = useState(-1);
  const wrapRef = useRef(null);
  const buttonRef = useRef(null);
  const itemRefs = useRef([]);

  const groups = moreMenuGroups({ pinned, canMove, canPin });
  const flat = groups.flat();
  const exportIndex = flat.findIndex((it) => it.id === 'export');

  const setOpen = useCallback(
    (next) => {
      setOpenState(next);
      onOpenChange?.(next);
      if (!next) {
        setSubOpen(false);
        setFocusIndex(-1);
      }
    },
    [onOpenChange]
  );

  const close = useCallback(
    (refocus = true) => {
      setOpen(false);
      if (refocus) buttonRef.current?.focus();
    },
    [setOpen]
  );

  // Outside click closes (the submenu lives inside the wrapper, so it counts as inside).
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) close(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, close]);

  useEffect(() => {
    if (open && focusIndex >= 0 && !subOpen) itemRefs.current[focusIndex]?.focus();
  }, [open, focusIndex, subOpen]);

  const openMenu = () => {
    setOpen(true);
    setFocusIndex(nextMenuIndex(flat.map((it) => !it.disabled), -1, 'Home'));
  };

  const run = (item, via = 'mouse') => {
    if (!item || item.disabled) return;
    if (item.submenu) {
      setFocusIndex(exportIndex);
      setSubOpen(via);
      return;
    }
    close();
    onAction(item.id);
  };

  const onMenuKeyDown = (e) => {
    if (subOpen) return; // the submenu handles its own keys
    if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) {
      e.preventDefault();
      setFocusIndex((i) => nextMenuIndex(flat.map((it) => !it.disabled), i, e.key));
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      close();
    } else if (e.key === 'Tab') {
      close(false);
    } else if (e.key === 'ArrowRight' && flat[focusIndex]?.submenu) {
      e.preventDefault();
      setSubOpen('keys');
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      run(flat[focusIndex], 'keys');
    }
  };

  let flatIndex = -1;
  return (
    <div className="doc-mm-wrap" ref={wrapRef}>
      <Tooltip content={t('docEditor.toolbar.more')} side="bottom" disabled={open}>
        <button
          ref={buttonRef}
          type="button"
          className={`doc-tbtn doc-tbtn--icon${open ? ' is-open' : ''}`}
          aria-label={t('docEditor.toolbar.more')}
          aria-haspopup="menu"
          aria-expanded={open}
          data-testid="doc-more-button"
          onClick={() => (open ? close(false) : openMenu())}
          onKeyDown={(e) => {
            if (!open && (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ')) {
              e.preventDefault();
              openMenu();
            }
          }}
        >
          <MoreHorizontal size={16} strokeWidth={1.6} aria-hidden />
        </button>
      </Tooltip>
      {open && (
        <div className="doc-mm" role="menu" aria-label={t('docEditor.menu.label')} onKeyDown={onMenuKeyDown} data-testid="doc-more-menu">
          {groups.map((group, g) => (
            <React.Fragment key={g}>
              {g > 0 && <div className="doc-mm-sep" role="separator" />}
              {group.map((item) => {
                flatIndex += 1;
                const i = flatIndex;
                const active = item.submenu && subOpen;
                return (
                  <button
                    key={item.id}
                    ref={(el) => {
                      itemRefs.current[i] = el;
                    }}
                    type="button"
                    role="menuitem"
                    tabIndex={i === focusIndex ? 0 : -1}
                    disabled={item.disabled}
                    aria-haspopup={item.submenu ? 'menu' : undefined}
                    aria-expanded={item.submenu ? subOpen : undefined}
                    className={`doc-mm-item${item.danger ? ' doc-mm-item--danger' : ''}${active ? ' is-active' : ''}`}
                    data-menu-id={item.id}
                    onMouseEnter={() => {
                      setFocusIndex(i);
                      if (!item.submenu) setSubOpen(false);
                      else if (!subOpen) setSubOpen('mouse');
                    }}
                    onClick={() => run(item)}
                  >
                    <MenuIcon id={item.id} pinned={pinned} />
                    <span className="doc-mm-label">{t(item.labelKey)}</span>
                    {item.kbd && <kbd className="doc-mm-shortcut">{item.kbd}</kbd>}
                    {item.submenu && <ChevronRight size={14} strokeWidth={1.6} className="doc-mm-arrow" aria-hidden />}
                  </button>
                );
              })}
            </React.Fragment>
          ))}
          {subOpen && (
            <ExportSubmenu
              anchor={itemRefs.current[exportIndex]}
              focusFirst={subOpen === 'keys'}
              onBack={() => {
                setSubOpen(false);
                itemRefs.current[exportIndex]?.focus();
              }}
              onClose={() => close()}
              onExport={(format, includeSubpages) => {
                close();
                onExport(format, includeSubpages);
              }}
            />
          )}
        </div>
      )}
    </div>
  );
}
